/**
 * Labstep
 *
 * @module grid/SmartTableProtocolCondition/components/ToolPanel/ProtocolValue/AddColumns
 * @desc Shared component for adding columns related to a protocol value
 */

import { EntitySearchMulti } from 'labstep-web/components/Entity/Search/Multi';
import { ICONS } from 'labstep-web/constants/icons';
import { Flex } from 'labstep-web/core/Flex';
import { SectionHeader } from 'labstep-web/core/Header';
import { useToolPanelContext } from 'labstep-web/grid/Index/components/ToolPanel/context';
import { EntityDataGridToolPanelListItem } from 'labstep-web/grid/Report/components/ToolPanel/ListItem';
import { getMetadataColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/metadata';
import { getProtocolValueAmountColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/amount';
import { getProtocolValueResourceColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/resource';
import { getProtocolValueResourceItemColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/resourceItem';
import { getProtocolValueResourceLocationColumnDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/resourceLocation';
import { getProtocolValueResourceItemStatusColDef } from 'labstep-web/grid/SmartTableProtocolCondition/coldefs/protocol-value/status';
import { ToolPanelState } from 'labstep-web/grid/SmartTableProtocolCondition/components/ToolPanel';
import type { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import { GridToolBarService } from 'labstep-web/grid/services/grid-toolbar.service';
import type { EntityView } from 'labstep-web/models/entity-view.model';
import type { Experiment } from 'labstep-web/models/experiment.model';
import { Metadata } from 'labstep-web/models/metadata';
import { MetadataType } from 'labstep-web/models/metadata/types';
import type { Protocol } from 'labstep-web/models/protocol.model';
import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import { Resource } from 'labstep-web/models/resource.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import type React from 'react';
import type { Dispatch, SetStateAction } from 'react';

export interface ProtocolValueAddColumnsProps {
  protocolValue: ProtocolValue;
  entityView: EntityView;
  gridSmartTableProtocolConditionService: GridSmartTableProtocolConditionService;
  colIds: string[];
  experimentOrProtocol: Experiment | Protocol;
  selectMetadata: (
    metadata: Metadata,
    parentEntityClass: string,
  ) => void;
  setProtocolValue: Dispatch<SetStateAction<ProtocolValue | null>>;
}

export const ProtocolValueAddColumns: React.FC<
  ProtocolValueAddColumnsProps
> = ({
  protocolValue,
  entityView,
  gridSmartTableProtocolConditionService,
  colIds,
  experimentOrProtocol,
  selectMetadata,
  setProtocolValue,
}) => {
  const toolPanelContext = useToolPanelContext();

  if (!protocolValue.reportingName) {
    return null;
  }

  const protocolValueAmountColDef = getProtocolValueAmountColumnDef(
    experimentOrProtocol,
    protocolValue.reportingName,
  );

  const protocolValueResourceNameColDef =
    getProtocolValueResourceColumnDef(
      experimentOrProtocol,
      protocolValue.reportingName,
    );

  const protocolValueResourceItemNameColDef =
    getProtocolValueResourceItemColumnDef(
      experimentOrProtocol,
      protocolValue.reportingName,
    );

  const protocolValueResourceLocationPathColumnDef =
    getProtocolValueResourceLocationColumnDef(
      experimentOrProtocol,
      protocolValue.reportingName,
    );

  const protocolValueResourceItemStatusColDef =
    getProtocolValueResourceItemStatusColDef(
      experimentOrProtocol,
      protocolValue.reportingName,
    );

  return (
    <>
      <small>Select which columns to show in the table.</small>
      <EntitySearchMulti
        staticLists={(searchQuery) => (
          <>
            <Flex column key="static">
              {GridToolBarService.showListItem(
                'amount',
                searchQuery,
              ) && (
                <EntityDataGridToolPanelListItem
                  icon={ICONS.protocol_value.info.amount_used}
                  actionIcon="eye"
                  text="Amount"
                  onClick={() => {
                    gridSmartTableProtocolConditionService.addColumnDef(
                      protocolValueAmountColDef,
                    );
                  }}
                  disabled={
                    protocolValueAmountColDef.colId
                      ? colIds.includes(
                          protocolValueAmountColDef.colId,
                        )
                      : false
                  }
                />
              )}
              {GridToolBarService.showListItem(
                'resource',
                searchQuery,
              ) && (
                <EntityDataGridToolPanelListItem
                  icon={ICONS.resource.primary}
                  actionIcon="eye"
                  text="Resource"
                  onClick={() => {
                    gridSmartTableProtocolConditionService.addColumnDef(
                      protocolValueResourceNameColDef,
                    );
                  }}
                  disabled={
                    protocolValueResourceNameColDef.colId
                      ? colIds.includes(
                          protocolValueResourceNameColDef.colId,
                        )
                      : false
                  }
                />
              )}
              {GridToolBarService.showListItem(
                'item',
                searchQuery,
              ) && (
                <EntityDataGridToolPanelListItem
                  icon={ICONS.resource_item.primary}
                  actionIcon="eye"
                  text="Item"
                  onClick={() => {
                    gridSmartTableProtocolConditionService.addColumnDef(
                      protocolValueResourceItemNameColDef,
                    );
                  }}
                  disabled={
                    protocolValueResourceItemNameColDef.colId
                      ? colIds.includes(
                          protocolValueResourceItemNameColDef.colId,
                        )
                      : false
                  }
                />
              )}
              {GridToolBarService.showListItem(
                'location',
                searchQuery,
              ) && (
                <EntityDataGridToolPanelListItem
                  icon={ICONS.resource_location.primary}
                  actionIcon="eye"
                  text="Location"
                  onClick={() => {
                    gridSmartTableProtocolConditionService.addColumnDef(
                      protocolValueResourceLocationPathColumnDef,
                    );
                  }}
                  disabled={
                    protocolValueResourceLocationPathColumnDef.colId
                      ? colIds.includes(
                          protocolValueResourceLocationPathColumnDef.colId,
                        )
                      : false
                  }
                />
              )}
              {GridToolBarService.showListItem(
                'status',
                searchQuery,
              ) && (
                <EntityDataGridToolPanelListItem
                  icon={'info circle'}
                  actionIcon="eye"
                  text="Status"
                  onClick={() => {
                    gridSmartTableProtocolConditionService.addColumnDef(
                      protocolValueResourceItemStatusColDef,
                    );
                  }}
                  disabled={
                    protocolValueResourceItemStatusColDef.colId
                      ? colIds.includes(
                          protocolValueResourceItemStatusColDef.colId,
                        )
                      : false
                  }
                />
              )}
            </Flex>
          </>
        )}
        lists={[
          {
            key: Resource.entityName,
            entityName: Metadata.entityName,
            searchField: 'label_like',
            params: {
              permission_class: Resource.entityName,
              group_id: entityView.group.id,
              group_by_label: 1,
              sort: 'label',
              count: 8,
              serializerGroups:
                'protocol_condition_reporting,paginator',
            },
            header: (
              <SectionHeader label="Resource Metadata Fields" />
            ),
            listItem: (entity: object) => {
              if (!protocolValue.reportingName) {
                return <p></p>;
              }

              const metadata = entity as Metadata;

              const colDef =
                metadata.type !== MetadataType.molecule &&
                getMetadataColDef(
                  experimentOrProtocol,
                  Resource.entityName,
                  (entity as Metadata).type,
                  (entity as Metadata).label || '',
                  protocolValue.reportingName,
                );
              return (
                <EntityDataGridToolPanelListItem
                  icon={
                    ICONS.metadata.type[
                      metadata.type as keyof typeof ICONS.metadata.type
                    ] || 'database'
                  }
                  actionIcon={
                    metadata.type === MetadataType.molecule
                      ? 'arrow right'
                      : 'eye'
                  }
                  text={metadata.label || 'Untitled data field'}
                  onClick={() => {
                    if (colDef) {
                      gridSmartTableProtocolConditionService.addColumnDef(
                        colDef,
                      );
                    } else {
                      setProtocolValue(protocolValue);
                      selectMetadata(metadata, Resource.entityName);

                      toolPanelContext?.setToolPanelState(
                        ToolPanelState.metadata_molecule,
                      );
                    }
                  }}
                  disabled={colDef && colIds.includes(colDef.colId!)}
                />
              );
            },
          },
          {
            key: ResourceItem.entityName,
            entityName: Metadata.entityName,
            searchField: 'label_like',
            params: {
              permission_class: ResourceItem.entityName,
              group_id: entityView.group.id,
              group_by_label: 1,
              sort: 'label',
              count: 8,
              serializerGroups:
                'protocol_condition_reporting,paginator',
            },
            header: <SectionHeader label="Item Metadata Fields" />,

            listItem: (entity: object) => {
              if (!protocolValue.reportingName) {
                return <p></p>;
              }

              const metadata = entity as Metadata;

              const colDef =
                metadata.type !== MetadataType.molecule &&
                getMetadataColDef(
                  experimentOrProtocol,
                  ResourceItem.entityName,
                  metadata.type,
                  metadata.label || '',
                  protocolValue.reportingName,
                );
              return (
                <EntityDataGridToolPanelListItem
                  icon={
                    ICONS.metadata.type[
                      metadata.type as keyof typeof ICONS.metadata.type
                    ] || 'database'
                  }
                  actionIcon={
                    metadata.type === MetadataType.molecule
                      ? 'arrow right'
                      : 'eye'
                  }
                  text={metadata.label || 'Untitled data field'}
                  onClick={() => {
                    if (colDef) {
                      gridSmartTableProtocolConditionService.addColumnDef(
                        colDef,
                      );
                    } else {
                      setProtocolValue(protocolValue);

                      selectMetadata(
                        metadata,
                        ResourceItem.entityName,
                      );

                      toolPanelContext?.setToolPanelState(
                        ToolPanelState.metadata_molecule,
                      );
                    }
                  }}
                  disabled={colDef && colIds.includes(colDef.colId!)}
                />
              );
            },
          },
        ]}
      />
    </>
  );
};
